import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { createTagForSelectedProducts, removeProductTagIds } from '@services/collectionService';

import { COLOR_OPTIONS } from '@/features/Scan/constants';
import CommonImage from '@/components/CommonImage';
import { useAddCommentToProductMutation } from '@/features/Scan/api/scanApi';
import { toastError } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import { useCommonTags } from '@/hooks/useCommonTags';
import InputField from '@/components/InputField';

const AddTagBottomPopup = ({
	title,
	allTagList = [],
	fetchAllTags,
	sampleId,
	onClose,
	selectedSingleSample,
	setSelectedSingleSample,
	updateProductTagOnSelect
}) => {
	const [activeColorCode, setActiveColorCode] = useState('#DBFFE5');
	const [text, setText] = useState('');
	const [comment, setComment] = useState('');
	const dispatch = useDispatch();
	const [addCommentToProduct, { isLoading: isAddingComment, error: addingCommentError, isSuccess }] =
		useAddCommentToProductMutation();

	const commentRef = useRef(null);
	const commonTags = useCommonTags([selectedSingleSample], [selectedSingleSample?.id]);

	const handleUpdateExistingTags = (payload = []) => {
		setSelectedSingleSample({ ...selectedSingleSample, tagResponseList: payload[0].tagResponseList });
	};

	useEffect(() => {
		if (selectedSingleSample?.collectionRemarks) {
			setComment(selectedSingleSample?.collectionRemarks);
		}
	}, [selectedSingleSample?.collectionRemarks]);

	useEffect(() => {
		if (isSuccess) {
			setText('');
			setSelectedSingleSample({ ...selectedSingleSample, collectionRemarks: comment });
			// setComment('');
			onClose();
		}
	}, [isSuccess]);

	const handleAddComment = () => {
		addCommentToProduct({
			id: sampleId,
			params: { remarks: comment }
		});
	};

	useEffect(() => {
		fetchAllTags(text);
	}, [text]);

	const handleLoader = async (callback) => {
		try {
			dispatch(openLoader());
			await callback();
		} finally {
			dispatch(closeLoader());
		}
	};

	const removeTag = (id) => {
		const params = `?productIdList=${sampleId}`;
		handleLoader(async () => {
			const data = await removeProductTagIds(params, id);
			handleUpdateExistingTags(data);
			updateProductTagOnSelect(data);
		});
	};

	const createTag = (id) => {
		handleLoader(async () => {
			const postData = { text, colorCode: activeColorCode, ...(id && { id }) };
			const requestBody = {
				productIdList: [sampleId],
				tagRequest: postData
			};

			const data = await createTagForSelectedProducts(requestBody);
			handleUpdateExistingTags(data);
			updateProductTagOnSelect(data);
			setText('');
		});
	};

	const handleCommentChange = (e) => {
		setComment(e.target.value);
		setSelectedSingleSample({ ...selectedSingleSample, collectionRemarks: e.target.value });
		// Adjust height automatically when user types
		e.target.style.height = 'auto';
		e.target.style.height = `${e.target.scrollHeight}px`;
	};

	const renderTag = (text, colorCode, onClick = null) => (
		<span className="status radius" style={{ background: colorCode }}>
			{text}
			{onClick && (
				<img
					onClick={onClick}
					src={`${process.env.PUBLIC_URL}/icons/chip-close.svg`}
					className="ml-1 cursor-pointer"
					alt="remove"
				/>
			)}
		</span>
	);

	const renderColorOption = (color) => (
		<span
			key={color}
			className={`single-color ${activeColorCode === color ? 'active' : ''}`}
			onClick={() => setActiveColorCode(color)}
			style={{ background: color }}
		/>
	);

	const renderExistingTags = () =>
		commonTags.map((item, index) => (
			<React.Fragment key={`tag_${index}`}>
				{renderTag(item.text, item.colorCode, () => removeTag(item.id))}
			</React.Fragment>
		));

	const renderAllTags = () =>
		allTagList.map((item, index) => (
			<p
				className="px-3 py-1 d-flex align-items-center cursor-pointer"
				key={`all_tags_${index}`}
				onClick={() => createTag(item.id)}
			>
				{renderTag(item.text, item.colorCode)}
			</p>
		));

	const renderCreateSection = () => {
		if (allTagList.length === 0) {
			return (
				<div className="create">
					<p className="px-3 py-1 d-flex align-items-center" onClick={() => createTag()}>
						<span className="semibold-14">Create</span>
						{renderTag(text, activeColorCode)}
					</p>
					<div className="colors">{COLOR_OPTIONS.map(renderColorOption)}</div>
				</div>
			);
		}
		return null;
	};

	return (
		<LoaderComponent loading={isAddingComment}>
			{addingCommentError && toastError(addingCommentError)}
			<div className="common-popup upload-design-popup shadow-24dp">
				<div className="common-popup-header d-flex justify-content-between">
					<h3 className="semibold-16 mb-0">{title}</h3>
					<CommonImage
						src="/icons/close.svg"
						alt="close"
						className="cursor-pointer"
						onClick={onClose}
					/>
				</div>

				<div>
					<div className="common-popup-body">
						<div className="container px-0">
							<div className="row">
								<div className="col-md-12 mb-2">
									<div className="select-tag-dropdown w-100 position-static shadow-none">
										<div className="py-2">
											<div className="selected-tags design-tags mb-2">
												{renderExistingTags()}
											</div>
											<div className="input-tag">
												<InputField
													type="text"
													autoComplete="off"
													placeholder="Select or create new"
													value={text}
													onChange={(e) => setText(e.target.value)}
													onKeyDown={(e) => e.key === 'Enter' && createTag()}
													withWrapper={false}
													withLabel={false}
												/>
												<img
													src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`}
													alt="dropdown"
												/>
											</div>
										</div>
										{renderCreateSection()}
										<div className="suggested-tags scroll-y-label mt-1">
											{renderAllTags()}
										</div>
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group mb-0">
										<textarea
											ref={commentRef}
											className=""
											name="description"
											placeholder="Comments"
											rows="3"
											autoComplete="off"
											value={comment}
											onChange={handleCommentChange}
											style={{ resize: 'none', overflow: 'hidden' }}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="modal-footer align-items-center justify-content-end">
					<div className="footer-buttons text-right">
						<button onClick={() => handleAddComment()} className="button tertiary size32 ml-2">
							Submit
						</button>
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default AddTagBottomPopup;
